/**
 * Resolves a host path into a URL NiiVue can fetch.
 *
 * `get_asset_url` is a backend round-trip (it path-checks against the case
 * roots before handing back a `convertFileSrc` URL), so it cannot be called
 * during render. While the command is mocked it returns an empty string, which
 * this hook passes through as `null` — the viewer's empty state, not an error.
 */

import * as React from "react";
import { getAssetUrl, splitError } from "@/ipc/api";

export interface AssetUrlResult {
  url: string | null;
  loading: boolean;
  error: string | null;
}

export function useAssetUrl(path: string | null): AssetUrlResult {
  const [state, setState] = React.useState<AssetUrlResult>({
    url: null,
    loading: false,
    error: null,
  });

  React.useEffect(() => {
    if (!path) {
      setState({ url: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ url: null, loading: true, error: null });
    void getAssetUrl(path)
      .then((result) => {
        if (cancelled) return;
        setState({ url: result.url || null, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ url: null, loading: false, error: splitError(error).message });
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return state;
}
